import { useCallback, useState } from 'react'
import { flushSync } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { Button, Dialog } from '~/ui'

interface UseUnsavedChangesWarningOptions {
  hasUnsavedChanges: boolean
  onSave?: () => void | Promise<void>
  onDiscard: () => void
}

/**
 * Hook to manage unsaved changes warning dialog.
 * Returns a function to trigger the close flow and the confirmation modal component.
 *
 * @example
 * const { handleClose, ConfirmationModal } = useUnsavedChangesWarning({
 *   hasUnsavedChanges: isDirty,
 *   onSave: async () => { await saveChanges() },
 *   onDiscard: () => { resetForm() }
 * })
 *
 * return (
 *   <>
 *     <button onClick={handleClose}>Close</button>
 *     <ConfirmationModal />
 *   </>
 * )
 */
export function useUnsavedChangesWarning({
  hasUnsavedChanges,
  onSave,
  onDiscard,
}: UseUnsavedChangesWarningOptions) {
  const { t } = useTranslation()
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setIsConfirmationOpen(true)
    } else {
      onDiscard()
    }
  }, [hasUnsavedChanges, onDiscard])

  const handleSave = useCallback(async () => {
    if (!onSave) return

    setIsSaving(true)
    try {
      await onSave()
      flushSync(() => {
        setIsConfirmationOpen(false)
      })
      onDiscard()
    } catch (error) {
      console.error('Failed to save changes:', error)
    } finally {
      setIsSaving(false)
    }
  }, [onSave, onDiscard])

  const handleDiscardAndClose = useCallback(() => {
    flushSync(() => {
      setIsConfirmationOpen(false)
    })
    onDiscard()
  }, [onDiscard])

  const ConfirmationModal = useCallback(() => {
    if (!isConfirmationOpen) return null

    return (
      <Dialog
        open={isConfirmationOpen}
        onOpenChange={(open) => {
          if (!open && !isSaving) setIsConfirmationOpen(false)
        }}
      >
        <Dialog.Content>
          <Dialog.Title>{t('common.unsaved_changes.title')}</Dialog.Title>

          <Dialog.Description asChild>
            <p className="text-foreground/64 text-xs font-medium">
              {t('common.unsaved_changes.description')}
            </p>
          </Dialog.Description>

          <Dialog.Actions>
            {onSave && (
              <Button
                type="button"
                variant="primary"
                isLoading={isSaving}
                onClick={handleSave}
              >
                {t('action.save')}
              </Button>
            )}

            <Button
              type="button"
              variant="outline"
              disabled={isSaving}
              onClick={handleDiscardAndClose}
            >
              {t('action.exit_without_saving')}
            </Button>
          </Dialog.Actions>
        </Dialog.Content>
      </Dialog>
    )
  }, [
    isConfirmationOpen,
    isSaving,
    t,
    onSave,
    handleSave,
    handleDiscardAndClose,
  ])

  return {
    handleClose,
    ConfirmationModal,
  }
}
